import { useMemo, useState } from "react";
import { X } from "lucide-react";
import { useTakeoff } from "./store";
import { getPageState } from "./pageState";
import { isPdf, INPUT_SM, LABEL, MONO } from "./theme";

const OVERLAY_COLORS = [
  { name: "Blue", hex: "#2563eb" },
  { name: "Red", hex: "#dc2626" },
  { name: "Green", hex: "#059669" },
  { name: "Purple", hex: "#7c3aed" },
  { name: "Orange", hex: "#ea580c" },
];

/**
 * Addendum overlay chooser — compares the open sheet against any page of
 * any drawing already uploaded to THIS job (search by file, folder, sheet
 * name or page number). Comparison aid only; never touches geometry.
 */
export default function OverlayPanel({ drawing, pageNum, overlay, setOverlay, pageText }) {
  const { doc, drawings, folders } = useTakeoff();
  const [q, setQ] = useState("");
  const [pick, setPick] = useState(null); // {drawing, page}
  const [pageQ, setPageQ] = useState("");
  const [pageResults, setPageResults] = useState(null);
  const [indexing, setIndexing] = useState("");

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle || !doc) return [];
    const out = [];
    for (const d of drawings) {
      if (!isPdf(d) && !d.mime?.startsWith?.("image/")) continue;
      const folder = folders.find((f) => f.folder_id === d.folder_id)?.name || "";
      for (const [key, pg] of Object.entries(doc.pages || {})) {
        const [fid, pgNum] = key.split(":");
        if (fid !== d.drawing_id) continue;
        const page = Number(pgNum);
        const hay = `${d.name} ${folder} ${pg.name || ""} page ${page}`.toLowerCase();
        if (hay.includes(needle) || String(page) === needle) {
          out.push({ drawing: d, page, sheet: pg.name || `Page ${page}`, folder });
          if (out.length >= 200) return out;
        }
      }
      // also match the file itself with page 1 even when no page state exists
      if (`${d.name} ${folder}`.toLowerCase().includes(needle) && !out.some((o) => o.drawing.drawing_id === d.drawing_id)) {
        out.push({ drawing: d, page: 1, sheet: getPageState(doc, d.drawing_id, 1).name, folder });
        if (out.length >= 200) return out;
      }
    }
    return out;
  }, [q, doc, drawings, folders]);

  const choose = async (r) => {
    let page = r.page;
    if (r.drawing.drawing_id === drawing?.drawing_id && page === pageNum) page = page + 1; // never overlay a page on itself
    const next = { fileId: r.drawing.drawing_id, page, name: r.drawing.name, baseColor: "#2563eb", overlayColor: "#dc2626", opacity: 60 };
    setOverlay(next);
    setPick({ drawing: r.drawing, page });
    setPageResults(null);
    setPageQ("");
  };

  const searchPages = async () => {
    const needle = pageQ.trim().toLowerCase();
    if (!needle || !pick || !isPdf(pick.drawing)) { setPageResults(null); return; }
    setIndexing("Indexing…");
    const found = [];
    try {
      const docProxy = await (await import("./pdfSetup")).openPdfDoc(pick.drawing.drawing_id);
      for (let pg = 1; pg <= docProxy.numPages; pg++) {
        setIndexing(`Indexing page ${pg} of ${docProxy.numPages}…`);
        const { spans } = await pageText(pick.drawing.drawing_id, pg);
        const text = spans.map((s) => s.str).join(" ").toLowerCase();
        const sheet = getPageState(doc, pick.drawing.drawing_id, pg).name.toLowerCase();
        if (text.includes(needle) || sheet.includes(needle) || String(pg) === needle) found.push(pg);
        if (found.length >= 50) break;
      }
    } catch { /* index failed — results stay empty */ }
    setIndexing("");
    setPageResults(found);
  };

  const setColor = (which, hex) => {
    const other = which === "baseColor" ? overlay.overlayColor : overlay.baseColor;
    if (hex === other) return; // never the same colour twice
    setOverlay({ ...overlay, [which]: hex });
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="overlay-panel">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Addendum overlay</p>

      {!overlay && (
        <div className="mt-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search this job's drawings — file, folder, sheet or page" className={INPUT_SM} data-testid="overlay-search" />
          <div className="mt-1.5 max-h-44 space-y-1 overflow-y-auto">
            {results.map((r) => (
              <button key={`${r.drawing.drawing_id}:${r.page}`} onClick={() => choose(r)}
                data-testid={`overlay-pick-${r.drawing.drawing_id}-${r.page}`}
                className="block w-full rounded-lg border border-slate-100 px-2 py-1.5 text-left hover:border-[#f97316]/40 hover:bg-orange-50/40">
                <span className="block truncate text-xs font-medium text-slate-800">{r.sheet}</span>
                <span className="block truncate text-[10px] text-slate-400">{r.drawing.name}{r.folder ? ` · ${r.folder}` : ""} · p.{r.page}</span>
              </button>
            ))}
            {q.trim() && results.length >= 200 && <p className="text-[10px] text-slate-400">200+ matches — keep typing to narrow it down.</p>}
            {q.trim() && !results.length && <p className="py-2 text-center text-[11px] text-slate-400">No sheets match in this job.</p>}
          </div>
        </div>
      )}

      {overlay && (
        <div className="mt-2 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="min-w-0 truncate text-xs font-semibold text-slate-700" data-testid="overlay-active-name">{overlay.name} · p.{overlay.page}</p>
            <button onClick={() => { setOverlay(null); setPick(null); }} className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:bg-slate-50" data-testid="overlay-remove">
              <X className="h-3 w-3" /> Remove overlay
            </button>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Page</label>
            <input type="number" min="1" value={overlay.page}
              onChange={(e) => {
                const n = Math.max(1, parseInt(e.target.value, 10) || 1);
                if (overlay.fileId === drawing?.drawing_id && n === pageNum) return;
                setOverlay({ ...overlay, page: n });
              }}
              className={`${INPUT_SM} w-20`} data-testid="overlay-page-input" />
            {isPdf(pick?.drawing || {}) && (
              <>
                <input value={pageQ} onChange={(e) => setPageQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchPages()}
                  placeholder="Search this PDF…" className={`${INPUT_SM} flex-1`} data-testid="overlay-page-search" />
                <button onClick={searchPages} className="rounded-md border border-slate-300 px-2 py-1 text-[10px] font-semibold uppercase text-slate-500 hover:bg-slate-50" data-testid="overlay-page-search-btn">Find</button>
              </>
            )}
          </div>
          {indexing && <p className="text-[10px] text-slate-400">{indexing}</p>}
          {pageResults && (
            <div className="flex flex-wrap gap-1">
              {pageResults.map((pg) => (
                <button key={pg} onClick={() => !(overlay.fileId === drawing?.drawing_id && pg === pageNum) && setOverlay({ ...overlay, page: pg })}
                  className="rounded-md border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600 hover:border-[#f97316] hover:text-[#f97316]" data-testid={`overlay-page-result-${pg}`}>
                  p.{pg} {getPageState(doc, overlay.fileId, pg).name}
                </button>
              ))}
              {!pageResults.length && <p className="text-[11px] text-slate-400">No pages match.</p>}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={LABEL}>Original ink</label>
              <select value={overlay.baseColor} onChange={(e) => setColor("baseColor", e.target.value)} className={INPUT_SM} data-testid="overlay-base-color">
                {OVERLAY_COLORS.map((c) => <option key={c.hex} value={c.hex} disabled={c.hex === overlay.overlayColor}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Overlay ink</label>
              <select value={overlay.overlayColor} onChange={(e) => setColor("overlayColor", e.target.value)} className={INPUT_SM} data-testid="overlay-ink-color">
                {OVERLAY_COLORS.map((c) => <option key={c.hex} value={c.hex} disabled={c.hex === overlay.baseColor}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={LABEL}>Opacity — <span className={MONO}>{overlay.opacity ?? 60}%</span></label>
            <input type="range" min="0" max="100" value={overlay.opacity ?? 60}
              onChange={(e) => setOverlay({ ...overlay, opacity: Number(e.target.value) })}
              className="w-full accent-[#f97316]" data-testid="overlay-opacity" />
          </div>
        </div>
      )}
    </div>
  );
}
